import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";

const BASE = `${import.meta.env.VITE_API_URL}/publisher/saved-calls`;

// Mirrors SavedCallRunStatus on the backend.
export type SavedCallRunStatus = "NeverRun" | "Running" | "Succeeded" | "Failed";

// Mirrors CountMatch on the backend (the baseline's count-comparison policy).
export type CountMatch = "NoErrors" | "Exact" | "AtLeast" | "Ignore";

// Mirrors API SavedCallResponse.
export interface SavedCall {
    id: string;
    orgId: string;
    projectId: string;
    projectName: string | null;
    workflowId: string;
    workflowName: string | null;
    name: string;
    description: string;
    inputs: Record<string, unknown>;
    projectContextId: string | null;
    lastRunAtUtc: string | null;
    lastRunId: string | null;
    lastRunStatus: SavedCallRunStatus;
    lastValidationResultId: string | null;
    // Whether this call has an active validation baseline, and (if so) its count policy — so the
    // list can show a "verified against a baseline" indicator without a per-row fetch.
    hasBaseline: boolean;
    baselineCountPolicy: CountMatch | null;
    createdAtUtc: string;
}

// Mirrors API OrgWorkflowResponse.
export interface OrgWorkflow {
    workflowId: string;
    workflowName: string;
    projectId: string;
    projectName: string;
}

export interface CreateSavedCallPayload {
    projectId: string;
    workflowId: string;
    name: string;
    inputs?: Record<string, unknown>;
    description?: string;
    projectContextId?: string;
}

export interface UpdateSavedCallPayload {
    name: string;
    inputs?: Record<string, unknown>;
    description?: string;
    projectContextId?: string;
}

// Mirrors API SavedCallRunSummary — a recent completed run, for the baseline run-picker.
export interface SavedCallRunSummary {
    runId: string;
    status: string;
    startedAt: string;
    durationMs: number;
}

// A call is "actively running" only while Running AND its run started recently. The recency bound
// stops a call stuck in Running (e.g. a lost completion event) from polling — and re-rendering the
// list — forever. Ten minutes covers any real run.
const ACTIVE_RUN_WINDOW_MS = 10 * 60 * 1000;

function isCallActivelyRunning(c: SavedCall): boolean {
    if (c.lastRunStatus !== "Running") return false;
    if (!c.lastRunAtUtc) return true;
    const startedMs = Date.parse(c.lastRunAtUtc);
    return Number.isNaN(startedMs) || Date.now() - startedMs < ACTIVE_RUN_WINDOW_MS;
}

// READ — the org's saved calls. Pass a projectId ref to scope to one project (the per-project Testing
// tab); omit it for the org-wide publisher hub. Polls only while a call is actively running (Running +
// recent) so its last-run badge converges once the async completion handler reconciles; stops otherwise.
export function useSavedCalls(projectId?: Ref<string | undefined>) {
    const { data, isFetching: savedCallsLoading, error, refetch: refetchSavedCalls } = useQuery({
        queryKey: computed(() => ["saved-calls", projectId?.value ?? null]),
        queryFn: () => {
            const qs = projectId?.value ? `?projectId=${encodeURIComponent(projectId.value)}` : "";
            return fetchWrapper.get<SavedCall[]>(`${BASE}${qs}`);
        },
        refetchInterval: (query) => {
            const calls = query.state.data as SavedCall[] | undefined;
            return calls?.some(isCallActivelyRunning) ? 5000 : false;
        },
    });
    const savedCalls = computed(() => data.value ?? []);

    return { savedCalls, savedCallsLoading, savedCallsError: error as Ref<ApiError | null>, refetchSavedCalls };
}

// READ — the org's workflows across all its projects, for the create form's picker (publisher hub).
// The per-project Testing tab sources workflows from useProjectWorkflows instead.
export function useOrgWorkflows() {
    const { data, isFetching: orgWorkflowsLoading, error } = useQuery({
        queryKey: ["org-workflows"],
        queryFn: () => fetchWrapper.get<OrgWorkflow[]>(`${BASE}/workflows`),
    });
    const orgWorkflows = computed(() => data.value ?? []);

    return { orgWorkflows, orgWorkflowsLoading, orgWorkflowsError: error as Ref<ApiError | null> };
}

// WRITE — create
export function useCreateSavedCall() {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<string, ApiError, CreateSavedCallPayload>({
        mutationFn: (payload) => fetchWrapper.post<string>(BASE, payload),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saved-calls"] }),
    });
    return {
        createSavedCall: mutate,
        createSavedCallAsync: mutateAsync,
        createSavedCallPending: isPending,
        createSavedCallError: error as Ref<ApiError | null>,
        createSavedCallReset: reset,
    };
}

// WRITE — update
export function useUpdateSavedCall() {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<
        void,
        ApiError,
        { callId: string; payload: UpdateSavedCallPayload }
    >({
        mutationFn: ({ callId, payload }) => fetchWrapper.put<void>(`${BASE}/${callId}`, payload),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saved-calls"] }),
    });
    return {
        updateSavedCall: mutate,
        updateSavedCallAsync: mutateAsync,
        updateSavedCallPending: isPending,
        updateSavedCallError: error as Ref<ApiError | null>,
        updateSavedCallReset: reset,
    };
}

// WRITE — delete
export function useDeleteSavedCall() {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error } = useMutation<void, ApiError, string>({
        mutationFn: (callId) => fetchWrapper.delete<void>(`${BASE}/${callId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saved-calls"] }),
    });
    return {
        deleteSavedCall: mutate,
        deleteSavedCallAsync: mutateAsync,
        deleteSavedCallPending: isPending,
        deleteSavedCallError: error as Ref<ApiError | null>,
    };
}

// WRITE — run now (returns the started workflow run id)
export function useRunSavedCall() {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error } = useMutation<string, ApiError, string>({
        mutationFn: (callId) => fetchWrapper.post<string>(`${BASE}/${callId}/run`),
        // Run updates last-run status on the call — refetch the list to reflect it.
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saved-calls"] }),
    });
    return {
        runSavedCall: mutate,
        runSavedCallAsync: mutateAsync,
        runSavedCallPending: isPending,
        runSavedCallError: error as Ref<ApiError | null>,
    };
}

// ── Validation baseline (per saved call) ─────────────────────────────────────────────────────────

// The validation outcome recorded for a run against its call's baseline.
export type ValidationOutcome = "Pass" | "Fail" | "Drift";

// Mirrors API BaselineServiceExpectation — one service's expected status + counts within a baseline.
export interface BaselineServiceExpectation {
    serviceId: string;
    serviceName: string;
    expectedStatus: string;
    expectedSuccess: number;
    expectedWarning: number;
    expectedError: number;
}

// Mirrors API WorkflowBaselineResponse — the expected outcome a saved call's runs are checked against.
export interface WorkflowBaseline {
    id: string;
    savedCallId: string;
    workflowId: string;
    runningProjectId: string;
    sourceRunId: string;
    expectedWorkflowStatus: string;
    countPolicy: CountMatch;
    isActive: boolean;
    serviceCount: number;
    services: BaselineServiceExpectation[];
    createdAtUtc: string;
}

// Mirrors API ServiceDiff — one service's expected-vs-actual comparison within a RunValidation.
export interface ServiceDiff {
    serviceId: string;
    serviceName: string;
    expectedStatus: string | null;
    actualStatus: string | null;
    expectedErrorCount: number;
    actualErrorCount: number;
    expectedWarningCount: number;
    actualWarningCount: number;
    expectedSuccessCount: number;
    actualSuccessCount: number;
    kind: "Match" | "Regressed" | "CountDrift" | "Missing" | "Added";
    detail: string | null;
}

// Mirrors API RunValidationResponse — a run's outcome against its call's baseline.
export interface RunValidation {
    id: string;
    savedCallId: string;
    runId: string;
    baselineId: string;
    workflowId: string;
    outcome: ValidationOutcome;
    serviceDiffs: ServiceDiff[];
    createdAtUtc: string;
}

// READ — recent completed runs for a saved call (newest first), for the baseline run-picker.
export function useSavedCallRuns(callId: Ref<string | null>) {
    const queryKey = computed(() => ["saved-call-runs", callId.value]);
    const { data, isFetching: runsLoading, error } = useQuery({
        queryKey,
        queryFn: () => fetchWrapper.get<SavedCallRunSummary[]>(`${BASE}/${callId.value}/runs`),
        enabled: computed(() => !!callId.value),
    });
    const runs = computed(() => data.value ?? []);
    return { runs, runsLoading, runsError: error as Ref<ApiError | null> };
}

// READ — the active baseline for a saved call (404 → null when none).
export function useSavedCallBaseline(callId: Ref<string | null>) {
    const queryKey = computed(() => ["saved-call-baseline", callId.value]);
    const { data: baseline, isFetching: baselineLoading, error } = useQuery({
        queryKey,
        queryFn: async () => {
            try {
                return await fetchWrapper.get<WorkflowBaseline>(`${BASE}/${callId.value}/baseline`);
            } catch (e) {
                // No baseline set for this call → treat as null.
                if ((e as ApiError)?.problem?.status === 404) return null;
                throw e;
            }
        },
        enabled: computed(() => !!callId.value),
    });
    return { baseline, baselineLoading, baselineError: error as Ref<ApiError | null> };
}

// READ — the validation result recorded for a call's run against its baseline (404 → null when the
// run wasn't validated, e.g. no baseline was set when it ran).
export function useRunValidation(callId: Ref<string | null>, runId: Ref<string | null>) {
    const queryKey = computed(() => ["run-validation", callId.value, runId.value]);
    const { data: validation, isFetching: validationLoading, error } = useQuery({
        queryKey,
        queryFn: async () => {
            try {
                return await fetchWrapper.get<RunValidation>(`${BASE}/${callId.value}/runs/${runId.value}/validation`);
            } catch (e) {
                if ((e as ApiError)?.problem?.status === 404) return null;
                throw e;
            }
        },
        enabled: computed(() => !!callId.value && !!runId.value),
    });
    return { validation, validationLoading, validationError: error as Ref<ApiError | null> };
}

// WRITE — promote a call's run to its baseline (runId defaults to the call's last run when omitted).
export function useSetSavedCallBaseline() {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error } = useMutation<
        string,
        ApiError,
        { callId: string; runId?: string; countPolicy?: CountMatch }
    >({
        mutationFn: ({ callId, runId, countPolicy }) =>
            fetchWrapper.post<string>(`${BASE}/${callId}/baseline`, {
                runId: runId ?? null,
                countPolicy: countPolicy ?? "NoErrors",
            }),
        onSuccess: (_id, vars) => {
            queryClient.invalidateQueries({ queryKey: ["saved-call-baseline", vars.callId] });
            queryClient.invalidateQueries({ queryKey: ["saved-calls"] });
        },
    });
    return {
        setBaseline: mutate,
        setBaselineAsync: mutateAsync,
        setBaselinePending: isPending,
        setBaselineError: error as Ref<ApiError | null>,
    };
}

// WRITE — clear a call's baseline
export function useClearSavedCallBaseline() {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error } = useMutation<void, ApiError, string>({
        mutationFn: (callId) => fetchWrapper.delete<void>(`${BASE}/${callId}/baseline`),
        onSuccess: (_v, callId) => {
            queryClient.invalidateQueries({ queryKey: ["saved-call-baseline", callId] });
            queryClient.invalidateQueries({ queryKey: ["saved-calls"] });
        },
    });
    return {
        clearBaseline: mutate,
        clearBaselineAsync: mutateAsync,
        clearBaselinePending: isPending,
        clearBaselineError: error as Ref<ApiError | null>,
    };
}
